import styled, { keyframes } from 'styled-components';

import { GRADIENTS } from '../../../../theme/tokens';

/**
 * Card-scoped reuse of the mobile Lumio glow waves. The soft wave is a TILED data-URI
 * SVG (one seamless period, real wave geometry + 30px blur) drifted with
 * `background-position` on a normal, non-GPU-promoted element. It is deliberately NOT
 * a 400%-wide transform layer: that needs a huge composited texture which Safari
 * culls (the topmost layer blinks out / "dropped frames"). background-position is a
 * paint, not a composited layer, so there is nothing to drop. See waves.ts
 * (BOTTOM_WAVE_TILES / waveTileImage).
 */
/** On-screen width of one wave period; the drift scrolls by exactly one tile so the
 *  repeated background loops with no seam. */
const TILE_PX = 820;
const waveScroll = keyframes`
  from { background-position-x: 0; }
  to { background-position-x: -${TILE_PX}px; }
`;
const waveScrollReverse = keyframes`
  from { background-position-x: -${TILE_PX}px; }
  to { background-position-x: 0; }
`;

/** Bottom band that clips + screen-blends the wave layers onto the card. */
export const WaveBand = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 78%;
  z-index: 0;
  overflow: hidden;
  pointer-events: none;
  mix-blend-mode: screen;
  border-radius: inherit;
  /* Clean blend group for the screen-blend (harmless; the Safari flicker fix lives
   * on WaveLayer — see the file header). */
  isolation: isolate;

  /* Dissolve the glow toward the top. Without this, overflow:hidden slices the
   * 30px-blurred wave layers flush at the band's clip edge, leaving a hard
   * horizontal line a third of the way up the card. A vertical mask keeps the
   * side/bottom containment (rounded corners, no horizontal bleed) while fading
   * the wave crest softly into the card. Stops
   * are mask ALPHA (black = keep, transparent = hide), not design colours, so
   * they stay un-tokenised, same rule as theme/mixins fadeMaskY. */
  -webkit-mask-image: linear-gradient(to top, #000 45%, transparent 96%);
  mask-image: linear-gradient(to top, #000 45%, transparent 96%);

  /* On the light card, screen-blend is invisible (it can only lighten). Switch to
   * multiply so the glow tints the white card — but keep it FAINT so banner text
   * stays easily readable (the waves are a subtle accent, not a wash). */
  :root[data-ows-theme='light'] & {
    mix-blend-mode: multiply;
    opacity: 0.22;
  }
`;

export const WaveLayer = styled.div<{
  $img: string;
  $dur: number;
  $reverse?: boolean;
  $blur: string;
  $opacity: number;
  $delay?: number;
}>`
  position: absolute;
  inset: 0;
  background-image: ${(p) => p.$img};
  background-repeat: repeat-x;
  background-position-x: 0;
  background-position-y: bottom;
  background-size: ${TILE_PX}px 100%;
  opacity: ${(p) => p.$opacity};
  filter: blur(${(p) => p.$blur});
  animation: ${(p) => (p.$reverse ? waveScrollReverse : waveScroll)} ${(p) => p.$dur}ms linear infinite;
  animation-delay: ${(p) => -(p.$delay ?? 0)}ms;

  @media (prefers-reduced-motion: reduce) {
    /* Hold a static frame — colour without perpetual motion. */
    animation: none;
    background-position-x: -${Math.round(TILE_PX * 0.37)}px;
  }
`;

/* ── Shooting stars ───────────────────────────────────────────────────────────
 * A subtle, minimalist accent over the right of the card: faint white→cyan streaks
 * that dart diagonally up-right (bottom→top, left→right), then rest invisibly for
 * the remainder of their cycle so they read as occasional shooting stars, not a
 * constant stream. Screen-blended, behind the text, hidden under reduced-motion. */
const shoot = keyframes`
  0% {
    opacity: 0;
    transform: translate3d(0, 0, 0) rotate(-53deg);
  }
  6% {
    opacity: var(--star-peak, 0.4);
  }
  20% {
    opacity: 0;
    transform: translate3d(150px, -200px, 0) rotate(-53deg);
  }
  100% {
    opacity: 0;
    transform: translate3d(150px, -200px, 0) rotate(-53deg);
  }
`;

/** Right-side band that hosts the streaks (screen-blended, behind content). */
export const StarField = styled.div`
  position: absolute;
  inset: 0 0 0 42%;
  z-index: 0;
  pointer-events: none;
  mix-blend-mode: screen;
  isolation: isolate; /* Safari: scope the blend group so the streaks don't flicker (see WaveBand). */

  /* Light theme: multiply so the streak (a saturated comet, no white) shows on white. */
  :root[data-ows-theme='light'] & {
    mix-blend-mode: multiply;
  }

  @media (prefers-reduced-motion: reduce) {
    display: none;
  }
`;

export const Star = styled.span<{
  $top: string;
  $left: string;
  $len: number;
  $dur: number;
  $delay: number;
  $peak: number;
}>`
  position: absolute;
  top: ${(p) => p.$top};
  left: ${(p) => p.$left};
  width: ${(p) => p.$len}px;
  height: 1.5px;
  border-radius: 2px;
  --star-peak: ${(p) => p.$peak};
  background: ${GRADIENTS.pulseStreak};
  opacity: 0;
  will-change: transform, opacity;
  transform: rotate(-53deg);
  -webkit-backface-visibility: hidden;
  backface-visibility: hidden;
  animation: ${shoot} ${(p) => p.$dur}ms linear infinite;
  animation-delay: -${(p) => p.$delay}ms;
`;
